'use client';
import { useTranslation } from '@/features/i18n';
import { Button } from '@/ui/buttons/Button';
import { logger } from '@/utils/loggerUtils';
import { HomeIcon } from '@heroicons/react/24/outline';
import {
  useActiveSubscriptions,
  useAuthentication,
} from '@vimond/features/authentication';
import { CouponValidationResult } from '@vimond/services/coupon';
import { checkVippsAgreementStatus } from '@vimond/services/default-payment-provider';
import { ProductGroup } from '@vimond/services/rest-api/types';
import { returnArrayOrSingleElementArray } from '@vimond/utils';
import { getEnvVar } from '@vimond/utils/env';
import { fetchAuthSession } from 'aws-amplify/auth';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApplyCoupon } from '../../Coupon/ApplyCoupon';
import Payment from '../../Payment/Payment';
import { ProductGroupsList } from '../../Products/ProductGroups/ProductGroupsList';
import { ProductsList } from '../../Products/ProductsList';
import { SubscribeStep } from './SubscribeStep';

type SubscriptionStep =
  | 'choose-product-group'
  | 'choose-product'
  | 'payment'
  | 'success'
  | 'error'
  | 'checking-vipps-status';

type SubscribeStepsProps = {
  productGroups: ProductGroup[];
};

type PeriodInputUnit = 'D' | 'M' | 'Y';
type PeriodInput = `P${number}${PeriodInputUnit}`;

const isValidPeriodInput = (input: string | number): input is PeriodInput => {
  const regex = /^P\d+[DWMY]$/;
  return typeof input === 'string' && regex.test(input);
};

const getPeriodUnit = (period: string | number): string => {
  if (!isValidPeriodInput(period)) {
    logger.error(`Invalid period input: ${period}`);
    return 'MONTH';
  }
  const periodUnit = period.charAt(2) as PeriodInputUnit;
  switch (periodUnit) {
    case 'D':
      return 'DAY';
    case 'M':
      return 'MONTH';
    case 'Y':
      return 'YEAR';
    default:
      return '';
  }
};

const getPeriodCount = (period: string | number): string => {
  if (!isValidPeriodInput(period)) {
    logger.error(`Invalid period input: ${period}`);
    return '1';
  }
  const periodCount = parseInt(period.charAt(1), 10);
  return isNaN(periodCount) ? '1' : `${periodCount}`;
};

const getProductsWithAppliedDiscounts = (
  productGroups: ProductGroup[],
  selectedProductGroupId: number | null,
  discountAppliedProducts: CouponValidationResult[],
) => {
  const products = returnArrayOrSingleElementArray(
    productGroups
      .find((pg) => pg.id === selectedProductGroupId)
      ?.productsUri.products.filter((p) => p.productStatus !== 'DISABLED') ??
      [],
  );

  if (!discountAppliedProducts || discountAppliedProducts.length === 0) {
    return products;
  }

  return products.map((product) => {
    const paymentId = product.productPayments?.productPaymentList?.[0]?.id;
    const discountObj = discountAppliedProducts.find(
      (d) =>
        d.productPaymentId === paymentId && d.discountedInitPrice !== undefined,
    );

    return discountObj
      ? {
          ...product,
          // Injecting discounted price temporarily into the product object for display purposes
          discountedInitPrice: discountObj.discountedInitPrice,
        }
      : product;
  });
};

const VIPPS_STATUS_CHECK_INTERVAL = 3000; // 3 seconds
const VIPPS_MAX_CHECKS = 10; // Maximum number of times to check status

export const SubscribeSteps = ({ productGroups }: SubscribeStepsProps) => {
  const { t } = useTranslation('translation');

  const router = useRouter();
  const { jwt } = useAuthentication();

  const searchParams = useSearchParams();
  const productGroupsIds = searchParams?.get('productGroupsIds')?.split(',');
  const redirect = searchParams?.get('redirect');
  const vippsStatus = searchParams?.get('vippsPayment');

  const { activeSubscriptions } = useActiveSubscriptions();

  const onPaymentSuccess = useCallback(async () => {
    const delay = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));
    await delay(10000);
    const authSession = await fetchAuthSession({
      forceRefresh: true,
    });
    logger.log('Refreshed session:', authSession);
    setStep('success');
  }, []);

  // Initialize the step based on whether a preselected product group ID exists
  // Only skip the first step for a single product group ID, not for comma-separated lists

  const preselectedProductGroupId = getEnvVar(
    'NEXT_PUBLIC_PRESELECTED_PRODUCT_GROUP_ID',
  );

  const initialStep: SubscriptionStep =
    !preselectedProductGroupId || preselectedProductGroupId.includes(',')
      ? 'choose-product-group' // No preselected ID or comma-separated list: start with product group selection
      : 'choose-product'; // Single ID: skip to product selection

  const [step, setStep] = useState<SubscriptionStep>(initialStep);

  const hasActiveSubscriptionForSelectedGroups = Boolean(
    activeSubscriptions?.some(
      (sub) =>
        productGroupsIds?.includes(`${sub.productGroupId}`) &&
        sub.status !== 'ENDED',
    ),
  );

  useEffect(() => {
    if (!hasActiveSubscriptionForSelectedGroups) {
      return;
    }

    if (step === 'success' || step === 'checking-vipps-status') {
      return;
    }

    router.replace('/');
  }, [hasActiveSubscriptionForSelectedGroups, router, step]);

  // Prepare preselected product group IDs if they exist in environment
  let initialProductGroupId: number | null = null;
  let filteredProductGroupIds: number[] = [];

  if (preselectedProductGroupId) {
    if (preselectedProductGroupId?.includes(',')) {
      // Multiple product group IDs (comma-separated)
      filteredProductGroupIds = preselectedProductGroupId
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id));

      // When it's a comma-separated list, don't preselect any product group
      initialProductGroupId = null;
    } else {
      // Single product group ID
      const parsedId = parseInt(preselectedProductGroupId, 10);
      if (!isNaN(parsedId)) {
        initialProductGroupId = parsedId;
      }
    }
  }

  // For UI display logic - true when we have multiple IDs that should be filtered
  const hasMultiplePreselectedIds = filteredProductGroupIds.length > 0;

  const [selectedProductGroupId, setSelectedProductGroupId] = useState<
    number | null
  >(initialProductGroupId);
  const selectedProductGroup = productGroups.find(
    (pg) => pg.id === selectedProductGroupId,
  );
  const selectedStripeProductIdMetadataEntry =
    selectedProductGroup?.metadata?.entries?.['stripe-product-id'];
  const selectedStripeProductId = (() => {
    if (Array.isArray(selectedStripeProductIdMetadataEntry)) {
      const el = selectedStripeProductIdMetadataEntry[0];
      if (el && typeof el === 'object' && 'value' in el) {
        return el && typeof (el as { value: unknown }).value === 'string'
          ? (el as { value: string }).value
          : '';
      }
      return '';
    }
    return '';
  })();
  const groupName = selectedProductGroup?.name;
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const selectedProduct = Array.isArray(
    selectedProductGroup?.productsUri.products,
  )
    ? selectedProductGroup?.productsUri.products.find(
        (p) => p.id === selectedProductId,
      )
    : selectedProductGroup?.productsUri.products;
  const selectedProductPaymentId =
    selectedProduct?.productPayments?.productPaymentList?.[0]?.id.toString() ??
    '';

  const productPaymentIds = useMemo(() => {
    if (!selectedProductGroup?.productsUri.products) return [];
    const paymentId = (item: {
      productPayments?: { productPaymentList?: { id?: number }[] };
    }): number | undefined => item.productPayments?.productPaymentList?.[0]?.id;
    return selectedProductGroup.productsUri.products
      .filter(
        (item) =>
          item.enabled === true &&
          typeof paymentId(item) === 'number' &&
          (!selectedProductPaymentId ||
            paymentId(item)?.toString() === selectedProductPaymentId),
      )
      .map((item) => paymentId(item)) as number[];
  }, [selectedProductGroup, selectedProductPaymentId]);

  // Check Vipps payment status with polling
  const checkVippsPaymentStatus = useCallback(
    async (agreementId: string, accessToken: string) => {
      let statusCheckCounter = 0;
      const checkStatus = async () => {
        try {
          const response = await checkVippsAgreementStatus(
            agreementId,
            accessToken,
          );

          if (response.status === 'ACTIVE') {
            // Payment successful
            await onPaymentSuccess();
            return true;
          } else if (response.status === 'STOPPED') {
            // Payment failed or was cancelled
            setStep('error');
            return true;
          } else if (response.status === 'PENDING') {
            // Still pending, continue polling if we haven't reached max attempts
            statusCheckCounter++;

            if (statusCheckCounter < VIPPS_MAX_CHECKS) {
              return false; // Continue checking
            } else {
              // We've reached max attempts, stop checking but don't consider it an error
              // The backend will still process it when complete
              setStep('error');
              return true;
            }
          }
        } catch (error) {
          logger.error('Error checking Vipps payment status:', error);
          setStep('error');
          throw error;
        }
        return false;
      };

      // Initial check
      if (await checkStatus()) {
        return;
      }

      // Setup polling
      return new Promise<void>((resolve, reject) => {
        const intervalId = setInterval(async () => {
          try {
            const shouldStop = await checkStatus();
            if (shouldStop) {
              clearInterval(intervalId);
              resolve();
            }
          } catch (error) {
            clearInterval(intervalId);
            reject(error as Error);
          }
        }, VIPPS_STATUS_CHECK_INTERVAL);
      });
    },
    [onPaymentSuccess],
  );

  // Handle Vipps callback via URL parameters
  useEffect(() => {
    const handleVippsCallback = async () => {
      // Only process Vipps callbacks if Vipps payments are enabled
      if (!(getEnvVar('NEXT_PUBLIC_VIPPS_PAYMENTS_ENABLED') === 'true')) {
        return;
      }

      // Check if this is a redirect from Vipps by looking for status parameter
      if (vippsStatus) {
        const agreementId = localStorage.getItem('vippsAgreementId');
        if (agreementId && jwt) {
          setStep('checking-vipps-status');
          try {
            await checkVippsPaymentStatus(agreementId, jwt);
          } catch (error) {
            logger.error('Error checking Vipps payment status:', error);
            setStep('error');
          } finally {
            // Clear the stored agreement ID
            localStorage.removeItem('vippsAgreementId');
          }
        }
      }
    };
    void handleVippsCallback();
  }, [vippsStatus, jwt, checkVippsPaymentStatus]);

  const [discountAppliedProducts, setDiscountAppliedProducts] = useState<
    CouponValidationResult[]
  >([]);
  const [appliedCouponCode, setAppliedCouponCode] = useState<
    string | undefined
  >(undefined);

  const isCouponEnabled = getEnvVar('NEXT_PUBLIC_VOUCHER_ENABLED') === 'true';

  const applyCouponComponent = isCouponEnabled && (
    <ApplyCoupon
      productPaymentIds={productPaymentIds}
      onCouponApplied={(results, code) => {
        setDiscountAppliedProducts(results);
        setAppliedCouponCode(code);
      }}
    />
  );

  const getDiscountedPriceForSelectedProduct = (): string | undefined => {
    if (!selectedProduct) return undefined;
    const selectedProductWithDiscount = discountAppliedProducts.find(
      (result) =>
        result.productPaymentId ===
        selectedProduct.productPayments?.productPaymentList?.[0]?.id,
    );
    return selectedProductWithDiscount
      ? `${selectedProduct.currency} ${selectedProductWithDiscount.discountedInitPrice}`
      : undefined;
  };

  if (step === 'success') {
    return (
      <div className="w-full pt-[100px] md:pt-[200px] px-0 md:px-[100px] lg:px-[200px] flex flex-col items-start gap-16">
        <h1 className="text-5xl font-bold text-text-primary">
          {t('subscriptions.thank-you')}
        </h1>
        <p className="text-3xl text-text-primary">
          {t('subscriptions.success-message')}
        </p>
        <h3 className="text-3xl font-bold text-text-primary">
          {t('subscriptions.happy-watching')}
        </h3>
        {redirect && (
          <div className="flex flex-col gap-4">
            <Button
              variant="primary"
              className="min-w-[250px]"
              onClick={() => router.replace(redirect)}
            >
              {t('watch-now')}
            </Button>
            <Link href="/">
              <HomeIcon className="h-6 w-6 hover:opacity-70 transition-opacity duration-300" />
            </Link>
          </div>
        )}
        {!redirect && (
          <Link href="/">
            <Button variant="primary">
              {t('subscriptions.browse-content')}
            </Button>
          </Link>
        )}
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="w-full pt-[100px] md:pt-[200px] px-0 md:px-[100px] lg:px-[200px] flex flex-col items-start gap-16">
        <h1 className="text-5xl font-bold text-text-primary">
          {t('subscriptions.error')}
        </h1>
        <p className="text-3xl text-text-primary">
          {t('subscriptions.error-message')}
        </p>
        <Button
          variant="primary"
          onClick={() => {
            // If there's a single preselected product group ID, go directly to choose-product step
            if (
              preselectedProductGroupId &&
              !preselectedProductGroupId.includes(',')
            ) {
              const parsedId = parseInt(preselectedProductGroupId, 10);
              setSelectedProductGroupId(parsedId);
              setSelectedProductId(null);
              setStep('choose-product');
            } else {
              setSelectedProductGroupId(null);
              setSelectedProductId(null);
              setStep('choose-product-group');
            }
          }}
        >
          {t('try-again')}
        </Button>
      </div>
    );
  }

  if (step === 'checking-vipps-status') {
    return (
      <div className="w-full pt-[100px] md:pt-[200px] px-0 md:px-[100px] lg:px-[200px] flex flex-col items-center gap-8">
        <h1 className="text-3xl font-bold text-text-primary mb-4">
          {t('subscriptions.payment-in-progress')}
        </h1>
      </div>
    );
  }

  return (
    <div className="w-full pt-4 px-0 md:px-25 lg:px-50 flex flex-col items-center gap-8">
      {(!getEnvVar('NEXT_PUBLIC_PRESELECTED_PRODUCT_GROUP_ID') ||
        hasMultiplePreselectedIds) && (
        <SubscribeStep
          heading={`1. ${t('subscriptions.choose-a-subscription')}`}
          choiceName={selectedProductGroup?.name}
          choiceDescription={selectedProductGroup?.description}
          isCurrentStep={step === 'choose-product-group'}
          setStepAsCurrent={
            selectedProductGroupId
              ? () => {
                  setSelectedProductGroupId(null);
                  setSelectedProductId(null);
                  setStep('choose-product-group');
                }
              : undefined
          }
        >
          <ProductGroupsList
            highlightedProductGroupsIds={productGroupsIds ?? []}
            productGroups={
              hasMultiplePreselectedIds
                ? productGroups.filter((pg) =>
                    filteredProductGroupIds.includes(pg.id),
                  )
                : productGroups
            }
            onSelectProductGroup={(id: number) => {
              setSelectedProductGroupId(id);
              setStep('choose-product');
            }}
          />
        </SubscribeStep>
      )}
      <SubscribeStep
        heading={`${
          getEnvVar('NEXT_PUBLIC_PRESELECTED_PRODUCT_GROUP_ID') &&
          !hasMultiplePreselectedIds
            ? '1'
            : '2'
        }. ${t('subscriptions.choose-a-product')}`}
        choiceName={
          selectedProduct &&
          `${selectedProduct.currency} ${selectedProduct.price}`
        }
        choiceDescription={selectedProduct?.paymentPlan.name}
        isCurrentStep={step === 'choose-product'}
        applyCouponComponent={applyCouponComponent}
        discountedPrice={getDiscountedPriceForSelectedProduct()}
        setStepAsCurrent={
          selectedProductId
            ? () => {
                setSelectedProductId(null);
                setStep('choose-product');
              }
            : undefined
        }
      >
        <ProductsList
          products={getProductsWithAppliedDiscounts(
            productGroups,
            selectedProductGroupId,
            discountAppliedProducts,
          )}
          onSelectProduct={(id: number) => {
            setSelectedProductId(id);
            setStep('payment');
          }}
        />
      </SubscribeStep>

      <SubscribeStep
        heading={`${
          getEnvVar('NEXT_PUBLIC_PRESELECTED_PRODUCT_GROUP_ID') ? '2' : '3'
        }. ${t('subscriptions.payment')}`}
        isCurrentStep={step === 'payment'}
      >
        {selectedProductId !== null && selectedProductGroupId !== null && (
          <Payment
            productId={selectedProductId}
            stripeProductId={selectedStripeProductId}
            productGroupId={selectedProductGroupId}
            productName={groupName ?? ''}
            productPaymentId={selectedProductPaymentId}
            productPrice={selectedProduct?.price ?? 0}
            intervalUnit={getPeriodUnit(
              selectedProduct?.paymentPlan.periodIso ??
                selectedProduct?.paymentPlan.period ??
                'P1M',
            )}
            intervalCount={getPeriodCount(
              selectedProduct?.paymentPlan.periodIso ??
                selectedProduct?.paymentPlan.period ??
                'P1M',
            )}
            currency={selectedProduct?.currency ?? 'NOK'}
            onSuccess={onPaymentSuccess}
            onError={() => setStep('error')}
            redirect={redirect}
            voucherCode={appliedCouponCode}
          />
        )}
      </SubscribeStep>
    </div>
  );
};
